import React, { useState, useEffect } from 'react';
import api from '../../../../utils/api';
import MainLayout from '../../../../components/layout/MainLayout';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import GroqApiKeySetup from '../../../../components/automation/GroqApiKeySetup';

const AIAutoReplyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [keyIsSet, setKeyIsSet] = useState(true);
  const [config, setConfig] = useState({
    isEnabled: false,
    botName: 'AI Assistant',
    businessName: '',
    oneLinerPurpose: '',
    tone: 'professional',
    supportHours: '',
    location: '',
    website: '',
    servicesText: '',
    faqsText: '',
    cannotDoText: '',
    responseTimeEstimate: '2–3 hours',
    agentPhone: '',
    supportEmail: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api('/ai-auto-reply/config');
      if (res.ok) {
        const data = await res.json();
        setConfig(prev => ({ ...prev, ...data }));
      }
      // Status is actually fetched via GroqApiKeySetup internally, but we can do a parallel check or just rely on the callback from GroqApiKeySetup
    } catch (err) {
      console.error(err);
      alert('Failed to load AI config');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      const newStatus = !config.isEnabled;
      const res = await api('/ai-auto-reply/toggle', { method: 'PATCH', body: { isEnabled: newStatus } });
      if (!res.ok) throw new Error('Failed to toggle');
      setConfig({ ...config, isEnabled: newStatus });
      alert(`AI Auto-Reply is now ${newStatus ? 'ON' : 'OFF'}`);
    } catch (err) {
      console.error(err);
      alert('Failed to toggle AI');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const res = await api('/ai-auto-reply/config', { method: 'POST', body: config });
      if (!res.ok) throw new Error('Failed to save config');
      alert('Bot configuration saved');
    } catch (err) {
      console.error(err);
      alert('Failed to save config');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({ ...prev, [name]: value }));
  };

  if (loading) return <MainLayout><div style={{ padding: '24px' }}>Loading...</div></MainLayout>;

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <button 
            onClick={() => navigate('/automation')} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>AI Auto-Reply Configuration</h1>
        </div>

        {!keyIsSet ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '24px', borderRadius: '12px', display: 'inline-block', marginBottom: '32px' }}>
              <AlertTriangle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#991b1b', margin: '0 0 8px 0' }}>API Key Required</h2>
              <p style={{ color: '#b91c1c', margin: 0 }}>You must configure your Groq API key before enabling AI features.</p>
            </div>
            <div style={{ textAlign: 'left' }}>
              <GroqApiKeySetup onKeyUpdate={setKeyIsSet} />
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '24px' }}>
              <GroqApiKeySetup onKeyUpdate={setKeyIsSet} />
            </div>

            {/* Section 1 - Master Toggle */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Master Toggle</h2>
            <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>Enable or disable the AI for all incoming messages.</p>
          </div>
          <button 
            onClick={handleToggle}
            style={{ 
              padding: '12px 24px', 
              borderRadius: '8px', 
              border: 'none', 
              cursor: 'pointer',
              background: config.isEnabled ? '#16a34a' : '#ef4444',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            {config.isEnabled ? 'AI is ON' : 'AI is OFF'}
          </button>
        </div>

        {/* Section 2 - Bot Configuration */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>Bot Personality & Knowledge</h2>
          
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Bot Name</label>
                <input name="botName" value={config.botName || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Business Name</label>
                <input name="businessName" value={config.businessName || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>One-Line Purpose</label>
                <input name="oneLinerPurpose" value={config.oneLinerPurpose || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} placeholder="e.g. Help customers book an appointment" />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Tone</label>
                <select name="tone" value={config.tone || 'professional'} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Support Hours</label>
                <input name="supportHours" value={config.supportHours || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Location</label>
                <input name="location" value={config.location || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Website</label>
                <input name="website" value={config.website || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Services/Products (List them clearly)</label>
              <textarea name="servicesText" value={config.servicesText || ''} onChange={handleChange} rows="4" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', resize: 'vertical' }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>FAQs (Q: ... A: ...)</label>
              <textarea name="faqsText" value={config.faqsText || ''} onChange={handleChange} rows="4" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', resize: 'vertical' }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>What the bot CANNOT do</label>
              <textarea name="cannotDoText" value={config.cannotDoText || ''} onChange={handleChange} rows="3" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', resize: 'vertical' }} placeholder="e.g. Cannot negotiate prices, cannot issue refunds directly." />
            </div>

            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '16px', marginBottom: '8px' }}>Escalation & Handoff Settings</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Response Time Estimate</label>
                <input name="responseTimeEstimate" value={config.responseTimeEstimate || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} placeholder="e.g. 2–3 hours" />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Agent WhatsApp</label>
                <input name="agentPhone" value={config.agentPhone || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Support Email</label>
                <input name="supportEmail" value={config.supportEmail || ''} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />
              </div>
            </div>

            <button type="submit" style={{ padding: '12px', background: '#3b82f6', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', marginTop: '16px' }}>
              Save Configuration
            </button>
          </form>

        </div>
        </>
        )}
      </div>
    </MainLayout>
  );
};

export default AIAutoReplyPage;
